import { Breakpoint } from '@altibox/design-system-component-lib';
// eslint-disable-next-line import/no-unresolved
import { TypographyProps } from '@altibox/design-system-component-lib/dist/components/typography/types';
import { PortableText, PortableTextComponents, PortableTextProps } from '@portabletext/react';
import React, { FC, HTMLAttributes } from 'react';
import { merge } from 'lodash';

import { BulletListElement } from 'src/components/bullet-points';
import { OrderedListElement } from 'src/components/ordered-list';
import { getVariableValue } from 'src/utils/portable-text-utils';

import {
  StyledBulletList,
  StyledContainer,
  StyledHeaderPrimary,
  StyledHeaderSecondary,
  StyledHeaderTertiary,
  StyledLink,
  StyledNormalText,
  StyledOrderedList,
  StyledSubtitlePrimary,
  StyledSubtitleSecondary,
  StyledSubtitleTertiary,
} from './customized-portable-text.styled';

type BlockStyle = 'normal' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'bullet' | 'number';

type TypographyRules = Record<BlockStyle, Pick<TypographyProps, 'variant' | 'maxBreakpoint'>>;

interface OwnProps {
  typographyRules?: Partial<TypographyRules>;
  variablesMap?: Record<string, React.ReactNode>;
}

const DEFAULT_TYPOGRAPHY_MAPPING_RULES: TypographyRules = {
  h1: { variant: 'headline1', maxBreakpoint: Breakpoint.TABLET },
  h2: { variant: 'headline2', maxBreakpoint: Breakpoint.DESKTOP },
  h3: { variant: 'headline3', maxBreakpoint: Breakpoint.DESKTOP },
  h4: { variant: 'headline4', maxBreakpoint: Breakpoint.DESKTOP },
  h5: { variant: 'headline5', maxBreakpoint: Breakpoint.DESKTOP },
  h6: { variant: 'uiText1', maxBreakpoint: Breakpoint.DESKTOP },
  normal: { variant: 'uiText3', maxBreakpoint: Breakpoint.TABLET },
  bullet: { variant: 'paragraph2', maxBreakpoint: Breakpoint.DESKTOP },
  // eslint-disable-next-line id-denylist
  number: { variant: 'paragraph2', maxBreakpoint: Breakpoint.DESKTOP },
};

type Props = OwnProps & Pick<PortableTextProps, 'value' | 'components'> & HTMLAttributes<HTMLDivElement>;

export const CustomizedPortableText: FC<Props> = (props) => {
  const { value, components, typographyRules: typographyRulesOverrides, variablesMap, ...rest } = props;
  const typographyRules = merge({}, DEFAULT_TYPOGRAPHY_MAPPING_RULES, typographyRulesOverrides);

  const defaultComponents: PortableTextComponents = {
    list: {
      bullet: ({ children }) => <StyledBulletList>{children}</StyledBulletList>,
      // eslint-disable-next-line id-denylist
      number: ({ children }) => <StyledOrderedList>{children}</StyledOrderedList>,
    },
    listItem: {
      bullet: ({ children }) => <BulletListElement {...typographyRules.bullet}>{children}</BulletListElement>,
      // eslint-disable-next-line id-denylist
      number: ({ children }) => <OrderedListElement {...typographyRules.number}>{children}</OrderedListElement>,
    },
    marks: {
      link: (linkProps) => (
        <StyledLink href={linkProps.value.href} target="_blank">
          {linkProps.children}
        </StyledLink>
      ),
      variable: ({ text }) => getVariableValue(text, variablesMap),
    },
    block: {
      h1: ({ children }) => (
        <StyledHeaderPrimary {...typographyRules.h1} component="h1">
          {children}
        </StyledHeaderPrimary>
      ),
      h2: ({ children }) => (
        <StyledHeaderSecondary {...typographyRules.h2} component="h2">
          {children}
        </StyledHeaderSecondary>
      ),
      h3: ({ children }) => (
        <StyledHeaderTertiary {...typographyRules.h3} component="h3">
          {children}
        </StyledHeaderTertiary>
      ),
      h4: ({ children }) => (
        <StyledSubtitlePrimary {...typographyRules.h4} component="h4">
          {children}
        </StyledSubtitlePrimary>
      ),
      h5: ({ children }) => (
        <StyledSubtitleSecondary {...typographyRules.h5} component="h5">
          {children}
        </StyledSubtitleSecondary>
      ),
      h6: ({ children }) => (
        <StyledSubtitleTertiary {...typographyRules.h6} component="h6">
          {children}
        </StyledSubtitleTertiary>
      ),
      normal: ({ children }) => (
        <StyledNormalText {...typographyRules.normal} component="p">
          {children}
        </StyledNormalText>
      ),
    },
  };

  return (
    <StyledContainer {...rest}>
      <PortableText components={{ ...defaultComponents, ...components }} value={value} />
    </StyledContainer>
  );
};
